import React, { useMemo, useState } from 'react';
import { Package, Download, Play, Lock, Star, Search, ArrowUpDown, CheckCircle2, Clock, X, Send, BookOpen, Video, FileText, Zap, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { CourseViewer } from './CourseViewer';
import { useLanguage } from '../../i18n/LanguageContext';
interface PurchasesTabProps {
  onNavigate: (page: string) => void;
}
type ProductType = 'курс' | 'pdf' | 'марафон' | 'запись' | 'чек-лист';
type AccessStatus = 'granted' | 'pending';
interface PurchasedProduct {
  id: string;
  title: string;
  type: ProductType;
  authorName: string;
  purchaseDate: string;
  dateSortKey: number;
  lastOpened: string | null;
  pricePaid: string;
  accessStatus: AccessStatus;
  progress: {
    current: number;
    total: number;
    percent: number;
  } | null;
  reviewed: boolean;
}
const TYPE_ICONS: Record<ProductType, {
  icon: React.ReactNode;
  bg: string;
}> = {
  курс: {
    icon: <BookOpen className="w-3.5 h-3.5 text-indigo-600" data-id="element-20118" />,
    bg: 'bg-indigo-50'
  },
  pdf: {
    icon: <FileText className="w-3.5 h-3.5 text-slate-600" data-id="element-20119" />,
    bg: 'bg-slate-100'
  },
  марафон: {
    icon: <Zap className="w-3.5 h-3.5 text-pink-600" data-id="element-20120" />,
    bg: 'bg-pink-50'
  },
  запись: {
    icon: <Video className="w-3.5 h-3.5 text-emerald-600" data-id="element-20121" />,
    bg: 'bg-emerald-50'
  },
  'чек-лист': {
    icon: <FileText className="w-3.5 h-3.5 text-rose-600" data-id="element-20122" />,
    bg: 'bg-rose-50'
  }
};
const mockProducts: PurchasedProduct[] = [{
  id: 'PROD-001',
  title: 'Основы ведической астрологии: с нуля до первых прогнозов',
  type: 'курс',
  authorName: 'Елена Смирнова',
  purchaseDate: '12 Окт 2024',
  dateSortKey: 20241012,
  lastOpened: 'Вчера',
  pricePaid: '15 000 ₽',
  accessStatus: 'granted',
  progress: {
    current: 7,
    total: 12,
    percent: 58
  },
  reviewed: false
}, {
  id: 'PROD-002',
  title: 'Гайд: Как пережить транзит Сатурна без потерь',
  type: 'pdf',
  authorName: 'Анна Коваль',
  purchaseDate: '05 Окт 2024',
  dateSortKey: 20241005,
  lastOpened: '3 дня назад',
  pricePaid: '1 490 ₽',
  accessStatus: 'granted',
  progress: null,
  reviewed: true
}, {
  id: 'PROD-003',
  title: 'Марафон: Пробуждение Венеры (Отношения и Финансы)',
  type: 'марафон',
  authorName: 'Елена Смирнова',
  purchaseDate: '01 Окт 2024',
  dateSortKey: 20241001,
  lastOpened: '2 дня назад',
  pricePaid: '4 900 ₽',
  accessStatus: 'granted',
  progress: {
    current: 14,
    total: 21,
    percent: 66
  },
  reviewed: false
}, {
  id: 'PROD-004',
  title: 'Запись вебинара: Финансовые коды в натальной карте',
  type: 'запись',
  authorName: 'Михаил Светлов',
  purchaseDate: '28 Сен 2024',
  dateSortKey: 20240928,
  lastOpened: null,
  pricePaid: '2 900 ₽',
  accessStatus: 'granted',
  progress: null,
  reviewed: false
}, {
  id: 'PROD-005',
  title: 'Чек-лист: Совместимость партнёров (Синастрия)',
  type: 'чек-лист',
  authorName: 'Анна Коваль',
  purchaseDate: 'Сегодня',
  dateSortKey: 20241015,
  lastOpened: null,
  pricePaid: '490 ₽',
  accessStatus: 'pending',
  progress: null,
  reviewed: false
}];
function getProductStatus(p: PurchasedProduct): 'in_progress' | 'completed' | 'new' | 'pending' {
  if (p.accessStatus === 'pending') return 'pending';
  if (p.progress && p.progress.percent >= 100) return 'completed';
  if (p.progress && p.progress.percent > 0) return 'in_progress';
  return 'new';
}
const PAGE_SIZE = 20;
export function PurchasesTab({
  onNavigate
}: PurchasesTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'new' | 'old'>('new');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewingCourseId, setViewingCourseId] = useState<string | null>(null);
  // Review state
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittedReviews, setSubmittedReviews] = useState<string[]>([]);
  const products = mockProducts;
  const inProgress = products.filter(p => getProductStatus(p) === 'in_progress');
  const filtered = useMemo(() => {
    let result = [...products];
    if (typeFilter !== 'all') {
      const typeMap: Record<string, ProductType[]> = {
        курсы: ['курс'],
        pdf: ['pdf', 'чек-лист'],
        записи: ['запись'],
        марафоны: ['марафон']
      };
      result = result.filter(p => typeMap[typeFilter]?.includes(p.type));
    }
    if (statusFilter !== 'all') result = result.filter(p => getProductStatus(p) === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.title.toLowerCase().includes(q) || p.authorName.toLowerCase().includes(q));
    }
    result.sort((a, b) => sortOrder === 'new' ? b.dateSortKey - a.dateSortKey : a.dateSortKey - b.dateSortKey);
    return result;
  }, [typeFilter, statusFilter, searchQuery, sortOrder]);
  const visibleItems = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const toggle = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
    if (reviewingId && reviewingId !== id) setReviewingId(null);
  };
  const handleAction = (p: PurchasedProduct) => {
    const status = getProductStatus(p);
    if (p.type === 'pdf' || p.type === 'чек-лист') {
      showToast(t('userdash.purchases.opening', {
        title: p.title
      }));
    } else {
      setViewingCourseId(p.id);
    }
  };
  const getActionLabel = (p: PurchasedProduct) => {
    if (p.accessStatus === 'pending') return null;
    const status = getProductStatus(p);
    if (p.type === 'pdf' || p.type === 'чек-лист') return t('userdash.purchases.open');
    if (status === 'in_progress') return t('userdash.purchases.continue');
    if (status === 'completed') return t('userdash.purchases.review');
    return t('userdash.purchases.start');
  };
  const handleSubmitReview = (id: string) => {
    if (reviewRating === 0) return;
    setSubmittedReviews(prev => [...prev, id]);
    setReviewingId(null);
    setReviewRating(0);
    setReviewText('');
    showToast(t('userdash.purchases.thanksForReview'));
  };
  const typeFilters = [{
    key: 'all',
    label: t('userdash.purchases.filterAll')
  }, {
    key: 'курсы',
    label: t('userdash.purchases.filterCourses')
  }, {
    key: 'pdf',
    label: t('userdash.purchases.filterPdf')
  }, {
    key: 'записи',
    label: t('userdash.purchases.filterRecords')
  }, {
    key: 'марафоны',
    label: t('userdash.purchases.filterMarathons')
  }];
  const statusFilters = [{
    key: 'all',
    label: t('userdash.purchases.statusAll')
  }, {
    key: 'in_progress',
    label: t('userdash.purchases.statusInProgress')
  }, {
    key: 'completed',
    label: t('userdash.purchases.statusCompleted')
  }, {
    key: 'new',
    label: t('userdash.purchases.statusNew')
  }];
  if (viewingCourseId) {
    const course = products.find(p => p.id === viewingCourseId);
    if (course) {
      return <div className="h-[calc(100vh-12rem)] min-h-[600px]" data-id="element-20123">
          <CourseViewer course={course} onBack={() => setViewingCourseId(null)} data-id="element-20124" />
        </div>;
    }
  }
  if (products.length === 0) {
    return <div className="space-y-6" data-id="element-20125">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20126">
          {t('userdash.purchases.title')}
        </h2>
        <div className="text-center py-16" data-id="element-20127">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3" data-id="element-20128">
            <Package className="w-7 h-7 text-slate-300" data-id="element-20129" />
          </div>
          <h3 className="font-bold text-slate-900 mb-1" data-id="element-20130">
            {t('userdash.purchases.noPurchases')}
          </h3>
          <p className="text-sm text-slate-500 mb-4" data-id="element-20131">
            {t('userdash.purchases.noPurchasesDesc')}
          </p>
          <Button size="sm" onClick={() => onNavigate('marketplace')} data-id="element-20132">
            {t('userdash.purchases.toCatalog')}
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-4 max-w-3xl pb-8" data-id="element-20133">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-20134">
        <div data-id="element-20135">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20136">
            {t('userdash.purchases.title')}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-20137">
            {products.length} {t('userdash.purchases.materialsCount')}
          </p>
        </div>
        <Button variant="outline" size="sm" className="text-xs" onClick={() => onNavigate('marketplace')} data-id="element-20138">
          <Package className="w-3.5 h-3.5 mr-1" data-id="element-20139" />{' '}
          {t('userdash.purchases.toCatalog')}
        </Button>
      </div>

      {/* Continue Learning */}
      {inProgress.length > 0 && <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-20140">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2" data-id="element-20141">
            <Play className="w-4 h-4 text-indigo-600" data-id="element-20142" />
            <span className="text-xs font-bold text-slate-900" data-id="element-20143">
              {t('userdash.purchases.continueLearning')}
            </span>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-20144">
            {inProgress.slice(0, 3).map(p => {
          const ti = TYPE_ICONS[p.type];
          return <div key={p.id} className="flex items-center gap-3 px-3 py-2.5" data-id="element-20145">
                  <div className={`w-8 h-8 rounded-lg ${ti.bg} flex items-center justify-center shrink-0`} data-id="element-20146">
                    {ti.icon}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-20147">
                    <div className="font-medium text-sm text-slate-900 truncate" data-id="element-20148">
                      {p.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1" data-id="element-20149">
                      <div className="flex-1 max-w-[100px] bg-slate-100 rounded-full h-1.5" data-id="element-20150">
                        <div className="bg-indigo-500 h-1.5 rounded-full" style={{
                    width: `${p.progress!.percent}%`
                  }} data-id="element-20151" />
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-20152">
                        {p.progress!.current}/{p.progress!.total}
                      </span>
                    </div>
                  </div>
                  <Button size="sm" className="text-xs px-3 h-7 shrink-0" onClick={() => handleAction(p)} data-id="element-20153">
                    <Play className="w-3.5 h-3.5 mr-1" data-id="element-20154" />{' '}
                    {t('userdash.purchases.continue')}
                  </Button>
                </div>;
        })}
          </div>
        </div>}

      {/* Search + Filters */}
      <div className="space-y-2" data-id="element-20155">
        <div className="relative" data-id="element-20156">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-20157" />
          <input type="text" placeholder={t('userdash.purchases.search')} value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setVisibleCount(PAGE_SIZE);
        }} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 bg-white" data-id="element-20158" />
        </div>
        <div className="flex items-center justify-between gap-2" data-id="element-20159">
          <div className="flex items-center gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden pb-0.5" data-id="element-20160">
            {typeFilters.map(f => <button key={f.key} onClick={() => {
            setTypeFilter(f.key);
            setVisibleCount(PAGE_SIZE);
          }} className={`px-2 py-1 rounded-lg text-[11px] font-medium transition-colors shrink-0 ${typeFilter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-20161">
                {f.label}
              </button>)}
            <span className="w-px h-4 bg-slate-200 shrink-0 mx-0.5" data-id="element-20162" />
            {statusFilters.map(f => <button key={f.key} onClick={() => {
            setStatusFilter(f.key);
            setVisibleCount(PAGE_SIZE);
          }} className={`px-2 py-1 rounded-lg text-[11px] font-medium transition-colors shrink-0 ${statusFilter === f.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`} data-id="element-20163">
                {f.label}
              </button>)}
          </div>
          <button onClick={() => setSortOrder(sortOrder === 'new' ? 'old' : 'new')} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors shrink-0" data-id="element-20164">
            <ArrowUpDown className="w-3 h-3" data-id="element-20165" />{' '}
            {sortOrder === 'new' ? t('userdash.purchases.sortNew') : t('userdash.purchases.sortOld')}
          </button>
        </div>
      </div>

      {/* Products List */}
      {filtered.length > 0 ? <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-20166">
          {visibleItems.map(product => {
        const ti = TYPE_ICONS[product.type];
        const status = getProductStatus(product);
        const actionLabel = getActionLabel(product);
        const isExpanded = expandedId === product.id;
        const wasReviewed = product.reviewed || submittedReviews.includes(product.id);
        const isReviewing = reviewingId === product.id;
        return <div key={product.id} data-id="element-20167">
                {/* Compact row */}
                <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(product.id)} data-id="element-20168">
                  <div className={`w-8 h-8 rounded-lg ${ti.bg} flex items-center justify-center shrink-0`} data-id="element-20169">
                    {ti.icon}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-20170">
                    <div className="flex items-center gap-1.5" data-id="element-20171">
                      <span className="font-medium text-sm text-slate-900 truncate" data-id="element-20172">
                        {product.title}
                      </span>
                    </div>
                    {product.progress && <div className="flex items-center gap-1.5 mt-0.5" data-id="element-20173">
                        <div className="w-16 bg-slate-100 rounded-full h-1" data-id="element-20174">
                          <div className={`h-1 rounded-full ${product.progress.percent >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{
                    width: `${product.progress.percent}%`
                  }} data-id="element-20175" />
                        </div>
                        <span className="text-[9px] text-slate-400" data-id="element-20176">
                          {product.progress.percent}%
                        </span>
                      </div>}
                  </div>

                  {/* Status badges */}
                  {status === 'pending' && <Badge variant="warning" size="sm" className="text-[8px] py-0 h-4 px-1 shrink-0" data-id="element-20177">
                      <Lock className="w-2.5 h-2.5 mr-0.5" data-id="element-20178" />{' '}
                      {t('userdash.purchases.pending')}
                    </Badge>}
                  {wasReviewed && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 shrink-0" data-id="element-20179" />}

                  <span className="text-[10px] text-slate-400 shrink-0 hidden sm:block" data-id="element-20180">
                    {product.pricePaid}
                  </span>

                  {actionLabel && <Button size="sm" className="text-[10px] h-6 px-2.5 shrink-0 hidden sm:flex" onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleAction(product);
            }} data-id="element-20181">
                      {actionLabel}
                    </Button>}

                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-20182" />
                </div>

                {/* Expanded details */}
                {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-20183">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-3" data-id="element-20184">
                      {/* Details */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]" data-id="element-20185">
                        <div data-id="element-20186">
                          <span className="text-slate-400 block" data-id="element-20187">
                            {t('userdash.purchases.author')}
                          </span>
                          <span className="text-slate-700 font-medium" data-id="element-20188">
                            {product.authorName}
                          </span>
                        </div>
                        <div data-id="element-20189">
                          <span className="text-slate-400 block" data-id="element-20190">
                            {t('userdash.purchases.bought')}
                          </span>
                          <span className="text-slate-700" data-id="element-20191">
                            {product.purchaseDate}
                          </span>
                        </div>
                        <div data-id="element-20192">
                          <span className="text-slate-400 block" data-id="element-20193">
                            {t('userdash.purchases.price')}
                          </span>
                          <span className="text-slate-700 font-medium" data-id="element-20194">
                            {product.pricePaid}
                          </span>
                        </div>
                        <div data-id="element-20195">
                          <span className="text-slate-400 block" data-id="element-20196">
                            {t('userdash.purchases.opened')}
                          </span>
                          <span className="text-slate-700" data-id="element-20197">
                            {product.lastOpened || t('userdash.purchases.notOpenedYet')}
                          </span>
                        </div>
                      </div>

                      {/* Progress */}
                      {product.progress && <div data-id="element-20198">
                          <div className="flex items-center justify-between text-[10px] mb-1" data-id="element-20199">
                            <span className="text-slate-500" data-id="element-20200">
                              {t('userdash.purchases.progress')}
                            </span>
                            <span className="text-slate-700 font-medium" data-id="element-20201">
                              {product.progress.current}/
                              {product.progress.total} ·{' '}
                              {product.progress.percent}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-1.5" data-id="element-20202">
                            <div className={`h-1.5 rounded-full ${product.progress.percent >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{
                    width: `${product.progress.percent}%`
                  }} data-id="element-20203" />
                          </div>
                        </div>}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-1" data-id="element-20204">
                        {status === 'pending' ? <button onClick={() => showToast(t('userdash.purchases.supportRequestSent'))} className="text-[10px] text-indigo-600 hover:underline" data-id="element-20205">
                            {t('userdash.purchases.checkAccessStatus')}
                          </button> : <>
                            <Button size="sm" className="text-xs h-7 px-3" onClick={() => handleAction(product)} data-id="element-20206">
                              <Play className="w-3 h-3 mr-1" data-id="element-20207" /> {actionLabel}
                            </Button>
                            {(product.type === 'pdf' || product.type === 'чек-лист') && <Button variant="outline" size="sm" className="text-xs h-7 px-3" onClick={() => showToast(t('userdash.purchases.downloading'))} data-id="element-20208">
                                <Download className="w-3 h-3 mr-1" data-id="element-20209" />{' '}
                                {t('userdash.purchases.download')}
                              </Button>}
                            {!wasReviewed && !isReviewing && <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => {
                    setReviewingId(product.id);
                    setReviewRating(0);
                    setReviewText('');
                  }} data-id="element-20210">
                                <Star className="w-3 h-3 mr-1" data-id="element-20211" />{' '}
                                {t('userdash.purchases.leaveReview')}
                              </Button>}
                          </>}
                      </div>

                      {/* Review form */}
                      {isReviewing && <div className="border-t border-slate-200 pt-2 space-y-2" data-id="element-20212">
                          <div className="flex items-center justify-between" data-id="element-20213">
                            <span className="text-xs font-medium text-slate-700" data-id="element-20214">
                              {t('userdash.purchases.rateMaterial')}
                            </span>
                            <button onClick={() => setReviewingId(null)} className="p-0.5 text-slate-400 hover:text-slate-600" data-id="element-20215">
                              <X className="w-3.5 h-3.5" data-id="element-20216" />
                            </button>
                          </div>
                          <div className="flex gap-0.5" data-id="element-20217">
                            {[1, 2, 3, 4, 5].map(s => <button key={s} onMouseEnter={() => setReviewHover(s)} onMouseLeave={() => setReviewHover(0)} onClick={() => setReviewRating(s)} className="p-0.5" data-id="element-20218">
                                <Star className={`w-5 h-5 transition-colors ${s <= (reviewHover || reviewRating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-20219" />
                              </button>)}
                          </div>
                          {reviewRating > 0 && <div className="flex gap-2" data-id="element-20220">
                              <input type="text" value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder={t('userdash.purchases.shareImpressions')} className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-500" data-id="element-20221" />
                              <Button size="sm" className="text-xs h-7 px-3" onClick={() => handleSubmitReview(product.id)} data-id="element-20222">
                                <Send className="w-3 h-3 mr-1" data-id="element-20223" />{' '}
                                {t('userdash.purchases.send')}
                              </Button>
                            </div>}
                        </div>}

                      {/* Review confirmation */}
                      {submittedReviews.includes(product.id) && !product.reviewed && <div className="flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg" data-id="element-20224">
                            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" data-id="element-20225" />{' '}
                            {t('userdash.purchases.thanksForReview')}
                          </div>}
                    </div>
                  </div>}
              </div>;
      })}
        </div> : <div className="text-center py-12" data-id="element-20226">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3" data-id="element-20227">
            <Package className="w-7 h-7 text-slate-300" data-id="element-20228" />
          </div>
          <h3 className="font-bold text-slate-900 mb-1 text-sm" data-id="element-20229">
            {searchQuery ? t('userdash.purchases.nothingFound') : t('userdash.purchases.noMaterials')}
          </h3>
          <p className="text-xs text-slate-500" data-id="element-20230">
            {searchQuery ? t('userdash.purchases.tryChangingQuery') : t('userdash.purchases.tryAnotherFilter')}
          </p>
        </div>}

      {/* Load More */}
      {hasMore && <div className="text-center" data-id="element-20231">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => setVisibleCount(v => v + PAGE_SIZE)} data-id="element-20232">
            {t('userdash.purchases.showMore')} ({filtered.length - visibleCount}{' '}
            {t('userdash.purchases.left')})
          </Button>
        </div>}

      {filtered.length > 0 && <div className="text-center text-[10px] text-slate-400" data-id="element-20233">
          {t('userdash.purchases.showing')}{' '}
          {Math.min(visibleCount, filtered.length)} {t('userdash.purchases.of')}{' '}
          {filtered.length}
        </div>}

      {/* Stats */}
      <div className="flex gap-3" data-id="element-20234">
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-20235">
          <div className="text-lg font-bold text-slate-900" data-id="element-20236">
            {products.length}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20237">
            {t('userdash.purchases.materialsCount')}
          </div>
        </div>
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-20238">
          <div className="text-lg font-bold text-indigo-600" data-id="element-20239">
            {inProgress.length}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20240">
            {t('userdash.purchases.inProgressCount')}
          </div>
        </div>
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-20241">
          <div className="text-lg font-bold text-emerald-600" data-id="element-20242">
            {products.filter(p => getProductStatus(p) === 'completed').length}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20243">
            {t('userdash.purchases.completedCount')}
          </div>
        </div>
      </div>
    </div>;
}